import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { pingApi } from '../../utils/api';

const RECHECK_INTERVAL = 30000; // auto-probe every 30s while hidden state is on

/**
 * Fixed banner shown when the backend API is unreachable (network-level
 * failure). Listens for 'ch:api-status' events emitted by utils/api.js.
 * Offers a manual Retry, and quietly re-probes every 30s so the banner
 * clears itself the moment the backend is back.
 */
export default function OfflineBanner() {
  const [unreachable, setUnreachable] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const visible = useRef(false);

  useEffect(() => {
    const onStatus = (e) => {
      const reachable = !!e.detail?.reachable;
      // Only react to down-transitions; recovery is decided by the probe so a
      // single lucky background request can't mask a still-down API.
      if (!reachable && visible.current) return;
      if (!reachable) {
        visible.current = true;
        setUnreachable(true);
      }
    };

    const probe = async () => {
      try {
        await pingApi();
        if (visible.current) {
          visible.current = false;
          setUnreachable(false);
          setRetrying(false);
        }
      } catch {
        // still down; keep the banner up
      }
    };

    window.addEventListener('ch:api-status', onStatus);

    let timer = null;
    if (unreachable) {
      timer = setInterval(probe, RECHECK_INTERVAL);
    }
    return () => {
      window.removeEventListener('ch:api-status', onStatus);
      if (timer) clearInterval(timer);
    };
  }, [unreachable]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await pingApi();
      visible.current = false;
      setUnreachable(false);
      setRetrying(false);
      // Reload so pages refetch their data now that the API is back.
      window.location.reload();
    } catch {
      setRetrying(false);
    }
  };

  return (
    <AnimatePresence>
      {unreachable && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed top-0 left-0 right-0 z-[10001] bg-[#B45309] text-white shadow-lg"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap text-center">
            <i className="ri-wifi-off-line text-base" aria-hidden="true" />
            <span className="text-xs sm:text-sm font-semibold">
              Having trouble reaching our servers — some content may be out of date.
            </span>
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-[11px] font-bold uppercase tracking-widest transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {retrying ? (
                <>
                  <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Checking…
                </>
              ) : (
                <>
                  <i className="ri-refresh-line" aria-hidden="true" />
                  Retry
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
